import { Component, Fragment, h, Prop, State } from '@stencil/core';
import { store } from '@stencil/redux';
import { Unsubscribe } from 'redux';
import { redeemGiftCard, removeGiftCard, initPayment, initForOrder } from '../../../redux/actions';
import { RootState } from '../../../redux/reducers';
import { App, GiftCard, PaymentMethod, RtrError } from '../../../redux/types';
import { ButtonSize } from '../../atoms/rentware-button/types';
import { formatPrice, t } from '../../../redux/utils';
import { SelectOption } from '../../../types/types';

@Component({
  tag: 'rtr-payment-giftcard',
  styleUrl: '../style.sass',
  shadow: false,
})
export class RtrPaymentGiftCard {
  @Prop() orderReference!: string;
  @Prop() paymentOptions;
  @Prop() onSepaNameChange;
  @Prop() validationOptionsError: boolean;
  @Prop() errors: RtrError[] = [];
  @Prop() secondaryPaymentMethod: PaymentMethod;
  @Prop() onSecondaryOptionChange: (item: SelectOption) => void;

  @Prop({ mutable: true }) app: App;
  @Prop({ mutable: true }) giftCard: GiftCard;

  @State() showPaymentOptions: boolean = true;
  @State() code: string;
  @State() error: boolean = false;

  unsubscribe!: Unsubscribe;
  redeemGiftCard!: typeof redeemGiftCard;
  removeGiftCard!: typeof removeGiftCard;
  initPayment!: typeof initPayment;
  initForOrder!: typeof initForOrder;

  componentWillLoad() {
    this.unsubscribe = store.mapStateToProps(this, (state: RootState) => {
      return {
        app: state.app,
        giftCard: state.payment.giftCard,
      };
    });

    store.mapDispatchToProps(this, {
      redeemGiftCard,
      removeGiftCard,
      initPayment,
      initForOrder,
    });
  }

  handleGiftCardCodeInput = (code: string) => {
    this.code = code;
  };

  handleGiftCardCodeRedeem = () => {
    this.redeemGiftCard({
      orderReference: this.orderReference,
      code: this.code,
      splitOption: this.app.paymentSplitOption,
      onSuccess: () => {
        this.error = false;
        this.initPayment({ orderReference: this.orderReference });
        this.initForOrder(null, this.orderReference);
      },
      onError: () => {
        this.error = true;
      },
    });
  };

  handleResetCode = () => {
    this.removeGiftCard({
      orderReference: this.orderReference,
      code: this.giftCard.code,
      onSuccess: () => {
        this.code = '';
        this.error = false;
        this.showPaymentOptions = true;
        this.initPayment({ orderReference: this.orderReference });
        this.initForOrder(null, this.orderReference);
        this.onSecondaryOptionChange(null);
      },
      onError: () => console.error('error'),
    });
  };

  handleSecondaryOptionChange = item => {
    this.showPaymentOptions = false;
    this.onSecondaryOptionChange(item);
  };

  handleShowChangeMethod = () => {
    this.showPaymentOptions = true;
    this.onSecondaryOptionChange(null);
  };

  get filteredPaymentOptions() {
    return this.paymentOptions.filter(option => option.value !== 'giftcard'); // we won't allow recurrence of giftcards
  }

  get giftcardInfo() {
    const used = formatPrice(this.giftCard.usedAmount);
    const total = formatPrice(this.giftCard.initialAmount);

    return (
      <p>
        {t('payment.giftCardBalanceInfo', {
          usedNow: used,
          total: total,
        })}
      </p>
    );
  }

  get insufficientInfo() {
    const amount = formatPrice(this.remainingAmountToPay);

    return (
      <div>
        <p innerHTML={t('payment.giftCardInsufficientInfo')} />
        <p innerHTML={t('payment.giftCardRestPaymentInfo', { amount: amount })} />
      </div>
    );
  }

  get remainingAmountToPay(): number {
    return this.app?.amountToPay?.withSubtractedGiftCard;
  }

  get paymentNeeded() {
    return this.remainingAmountToPay > 0;
  }

  render() {
    return (
      <Fragment>
        <rentware-spacer size="L" />

        {!this.giftCard && (
          <Fragment>
            <rtr-input
              onInput={e => this.handleGiftCardCodeInput(e.target.value)}
              type="text"
              name="giftcard-code"
              label={t('payment.giftCardCodeLabel')}
              placeholder={t('payment.giftCardCodePlaceholder')}
            />

            {this.error && <span class="error">{t('payment.giftCardCodeInvalid')}</span>}
            <rentware-spacer size="M" />

            <rentware-button appearance={{ size: ButtonSize.L, fullWidth: true }} buttonText={t('payment.giftCardRedeemButton')} onClick={this.handleGiftCardCodeRedeem} />
          </Fragment>
        )}

        {this.giftCard && (
          <Fragment>
            {this.giftcardInfo}

            <rentware-spacer size="M" />
            <div class="RTPGiftCardCodeField">
              {this.giftCard.code}

              <div class="RTPcodeXButton" tabIndex={0} onClick={this.handleResetCode}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4.6967 4.6783L15.3033 15.2849M15.3033 4.6783L4.6967 15.2849" stroke="#FF0066" stroke-linecap="round" />
                </svg>
              </div>
            </div>

            <rentware-spacer size="M" />

            {this.paymentNeeded && this.insufficientInfo}

            {this.paymentNeeded && !!this.paymentOptions && (
              <Fragment>
                <rentware-spacer size="M" />

                <rtr-payment-options
                  allowChange={true}
                  paymentOptions={this.filteredPaymentOptions}
                  currentMethod={this.secondaryPaymentMethod}
                  showPaymentOptions={this.showPaymentOptions}
                  onOptionChange={this.handleSecondaryOptionChange}
                  onShowChangeMethod={this.handleShowChangeMethod}
                  validationOptionsError={this.validationOptionsError}
                />

                {!this.showPaymentOptions && (
                  <rtr-payment-method
                    orderReference={this.orderReference}
                    paymentOptions={this.filteredPaymentOptions}
                    currentMethod={this.secondaryPaymentMethod}
                    onSepaNameChange={this.onSepaNameChange}
                    errors={this.errors}
                  />
                )}
              </Fragment>
            )}
          </Fragment>
        )}

        <rentware-spacer size="S" />
      </Fragment>
    );
  }
}
